import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { TaskWithRelations } from '@shared/schema';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { useEffect } from 'react';
import { CheckCircle, Clock, HelpCircle, User, Users } from 'lucide-react';

interface TaskHelpRequestsProps {
  userId?: string;
  showOnlyStatus?: 'need_help' | 'help_done';
}

const STATUS_COLORS = {
  need_help: 'bg-yellow-100 text-yellow-800',
  help_done: 'bg-green-100 text-green-800',
};

const STATUS_LABELS = {
  need_help: 'Help Asked',
  help_done: 'Help Done',
};

const STATUS_ICONS = {
  need_help: HelpCircle,
  help_done: CheckCircle,
};

export default function TaskHelpRequests({
  userId,
  showOnlyStatus,
}: TaskHelpRequestsProps) {
  const { toast } = useToast();
  const queryClientHook = useQueryClient();

  const {
    data: tasks = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['/api/tasks'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/tasks');
      return response.json() as Promise<TaskWithRelations[]>;
    },
    staleTime: 0, // Always consider data stale so it refetches when invalidated
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gets focus
    refetchOnReconnect: true, // Refetch when network reconnects
    gcTime: 5 * 60 * 1000, // Keep data in cache for 5 minutes
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to fetch tasks',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4 sm:p-8">
        <div className="text-sm sm:text-base text-gray-500">
          Loading task help requests...
        </div>
      </div>
    );
  }

  // Filter tasks for help-related statuses
  let helpTasks = tasks.filter(
    task => task.status === 'need_help' || task.status === 'help_done'
  );

  // Filter by specific status if showOnlyStatus is provided
  if (showOnlyStatus) {
    helpTasks = helpTasks.filter(task => task.status === showOnlyStatus);
  }

  // Further filter by user if userId is provided
  const filteredTasks = userId
    ? helpTasks.filter(
        task =>
          task.assignedTo === userId ||
          task.createdBy === userId ||
          (task as any).helpers?.some((helper: any) => helper.id === userId)
      )
    : helpTasks;

  if (filteredTasks.length === 0) {
    return (
      <div className="text-center p-4 sm:p-8">
        <HelpCircle className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3 sm:mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
          No Task Help Requests
        </h3>
        <p className="text-sm text-gray-500 px-4">
          {userId
            ? "You haven't requested or provided help on any tasks yet."
            : 'No task help requests found.'}
        </p>
      </div>
    );
  }

  // Group tasks by status
  const needHelpTasks = filteredTasks.filter(
    task => task.status === 'need_help'
  );
  const helpDoneTasks = filteredTasks.filter(
    task => task.status === 'help_done'
  );

  // Render based on showOnlyStatus prop
  if (showOnlyStatus === 'need_help') {
    return (
      <div className="space-y-6">
        {needHelpTasks.length > 0 ? (
          <div className="space-y-3">
            {needHelpTasks.map(task => {
              const StatusIcon =
                STATUS_ICONS[task.status as keyof typeof STATUS_ICONS];
              return (
                <Card key={task.id} className="border-l-4 border-l-yellow-400">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg break-words">
                          {task.title}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge
                            className={
                              STATUS_COLORS[
                                task.status as keyof typeof STATUS_COLORS
                              ]
                            }
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {
                              STATUS_LABELS[
                                task.status as keyof typeof STATUS_LABELS
                              ]
                            }
                          </Badge>
                          {task.priority && (
                            <Badge variant="outline" className="text-xs">
                              Priority {task.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 flex items-center flex-shrink-0">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">
                          {formatDistanceToNow(
                            new Date(task.updatedAt || task.createdAt),
                            { addSuffix: true }
                          )}
                        </span>
                        <span className="sm:hidden">
                          {
                            formatDistanceToNow(
                              new Date(task.updatedAt || task.createdAt),
                              { addSuffix: true }
                            ).split(' ')[0]
                          }
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {task.description && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                        {task.description}
                      </p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          Assigned to: {task.assignee?.firstName}{' '}
                          {task.assignee?.lastName || 'Unassigned'}
                        </span>
                      </div>
                      {(task as any).helpers?.length > 0 && (
                        <div className="flex items-center min-w-0">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            Helpers:{' '}
                            {(task as any).helpers
                              .map((h: any) => `${h.firstName} ${h.lastName}`)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                    {task.dueDate && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-500">
                        Due:{' '}
                        {new Date(task.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center p-4 sm:p-8">
            <HelpCircle className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              No Help Asked Tasks
            </h3>
            <p className="text-sm text-gray-500 px-4">
              {userId
                ? "You haven't requested help on any tasks yet."
                : 'No tasks are currently asking for help.'}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (showOnlyStatus === 'help_done') {
    return (
      <div className="space-y-6">
        {helpDoneTasks.length > 0 ? (
          <div className="space-y-3">
            {helpDoneTasks.map(task => {
              const StatusIcon =
                STATUS_ICONS[task.status as keyof typeof STATUS_ICONS];
              return (
                <Card key={task.id} className="border-l-4 border-l-green-400">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg break-words">
                          {task.title}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge
                            className={
                              STATUS_COLORS[
                                task.status as keyof typeof STATUS_COLORS
                              ]
                            }
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {
                              STATUS_LABELS[
                                task.status as keyof typeof STATUS_LABELS
                              ]
                            }
                          </Badge>
                          {task.priority && (
                            <Badge variant="outline" className="text-xs">
                              Priority {task.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 flex items-center flex-shrink-0">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">
                          {formatDistanceToNow(
                            new Date(task.updatedAt || task.createdAt),
                            { addSuffix: true }
                          )}
                        </span>
                        <span className="sm:hidden">
                          {
                            formatDistanceToNow(
                              new Date(task.updatedAt || task.createdAt),
                              { addSuffix: true }
                            ).split(' ')[0]
                          }
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {task.description && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                        {task.description}
                      </p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          Assigned to: {task.assignee?.firstName}{' '}
                          {task.assignee?.lastName || 'Unassigned'}
                        </span>
                      </div>
                      {(task as any).helpers?.length > 0 && (
                        <div className="flex items-center min-w-0">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            Helpers:{' '}
                            {(task as any).helpers
                              .map((h: any) => `${h.firstName} ${h.lastName}`)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                    {task.dueDate && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-500">
                        Due:{' '}
                        {new Date(task.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center p-4 sm:p-8">
            <CheckCircle className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              No Help Done Tasks
            </h3>
            <p className="text-sm text-gray-500 px-4">
              {userId
                ? "You haven't provided help on any tasks yet."
                : 'No tasks have been marked as help done yet.'}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Default: show both sections (for backward compatibility)
  return (
    <div className="space-y-6">
      {/* Help Asked Tasks */}
      {needHelpTasks.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <HelpCircle className="w-5 h-5 mr-2 text-yellow-600" />
            Help Asked ({needHelpTasks.length})
          </h3>
          <div className="space-y-3">
            {needHelpTasks.map(task => {
              const StatusIcon =
                STATUS_ICONS[task.status as keyof typeof STATUS_ICONS];
              return (
                <Card key={task.id} className="border-l-4 border-l-yellow-400">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg break-words">
                          {task.title}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge
                            className={
                              STATUS_COLORS[
                                task.status as keyof typeof STATUS_COLORS
                              ]
                            }
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {
                              STATUS_LABELS[
                                task.status as keyof typeof STATUS_LABELS
                              ]
                            }
                          </Badge>
                          {task.priority && (
                            <Badge variant="outline" className="text-xs">
                              Priority {task.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 flex items-center flex-shrink-0">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">
                          {formatDistanceToNow(
                            new Date(task.updatedAt || task.createdAt),
                            { addSuffix: true }
                          )}
                        </span>
                        <span className="sm:hidden">
                          {
                            formatDistanceToNow(
                              new Date(task.updatedAt || task.createdAt),
                              { addSuffix: true }
                            ).split(' ')[0]
                          }
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {task.description && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                        {task.description}
                      </p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          Assigned to: {task.assignee?.firstName}{' '}
                          {task.assignee?.lastName || 'Unassigned'}
                        </span>
                      </div>
                      {(task as any).helpers?.length > 0 && (
                        <div className="flex items-center min-w-0">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            Helpers:{' '}
                            {(task as any).helpers
                              .map((h: any) => `${h.firstName} ${h.lastName}`)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                    {task.dueDate && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-500">
                        Due:{' '}
                        {new Date(task.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Help Done Tasks */}
      {helpDoneTasks.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
            Help Done ({helpDoneTasks.length})
          </h3>
          <div className="space-y-3">
            {helpDoneTasks.map(task => {
              const StatusIcon =
                STATUS_ICONS[task.status as keyof typeof STATUS_ICONS];
              return (
                <Card key={task.id} className="border-l-4 border-l-green-400">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg break-words">
                          {task.title}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge
                            className={
                              STATUS_COLORS[
                                task.status as keyof typeof STATUS_COLORS
                              ]
                            }
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {
                              STATUS_LABELS[
                                task.status as keyof typeof STATUS_LABELS
                              ]
                            }
                          </Badge>
                          {task.priority && (
                            <Badge variant="outline" className="text-xs">
                              Priority {task.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 flex items-center flex-shrink-0">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">
                          {formatDistanceToNow(
                            new Date(task.updatedAt || task.createdAt),
                            { addSuffix: true }
                          )}
                        </span>
                        <span className="sm:hidden">
                          {
                            formatDistanceToNow(
                              new Date(task.updatedAt || task.createdAt),
                              { addSuffix: true }
                            ).split(' ')[0]
                          }
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {task.description && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                        {task.description}
                      </p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          Assigned to: {task.assignee?.firstName}{' '}
                          {task.assignee?.lastName || 'Unassigned'}
                        </span>
                      </div>
                      {(task as any).helpers?.length > 0 && (
                        <div className="flex items-center min-w-0">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            Helpers:{' '}
                            {(task as any).helpers
                              .map((h: any) => `${h.firstName} ${h.lastName}`)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                    {task.dueDate && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-500">
                        Due:{' '}
                        {new Date(task.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
